<script setup lang="ts">
import type { components as StrapiComponents } from '@core/types/clients/strapi'
import type { StrapiBlock, StrapiDynamicRendererProps } from '@nimbus/types'
import { uniqWith, isEqual } from 'es-toolkit'
import { computed, useSeoSchema, renderStrapiBlocks } from '#imports'

const props = defineProps<StrapiDynamicRendererProps>()

const seoSchema = useSeoSchema()

const maxWidth = computed(() => props.maxWidth ? `${props.maxWidth}px` : null)
const color = computed(() => props.color || null)

type ExtendedDynamicFaqComponent = Omit<StrapiComponents['schemas']['DynamicFaqComponent'], 'faqSections'> & {
    faqSections?: StrapiComponents['schemas']['Faq'][]
}

function getFaqItems(faq: ExtendedDynamicFaqComponent) {
    return {
        data: faq?.faqSections?.flatMap(section => section?.faqItems?.filter(Boolean)) || []
    }
}

// Schema
async function createSeoSchema() {
    const preparedData = props.content?.reduce((acc: { faqItems: ReturnType<typeof getFaqItems>[] }, child) => {
        if (child.__component === 'dynamic.faq') {
            // There can be multiple FAQs on a page, but only one FAQPage instance is allowed in the schema,
            // therefore faq items are concatenated
            const faqItems = getFaqItems(child as ExtendedDynamicFaqComponent)
            acc.faqItems.push(faqItems)
        }

        return acc
    }, {
        faqItems: []
    }) || { faqItems: [] }

    // FAQ
    seoSchema.add({
        '@type': 'FAQPage',
        'mainEntity': await Promise.all(
            // Make sure we have items before trying to map them
            (preparedData.faqItems.length > 0
                ? uniqWith(preparedData.faqItems as readonly any[], isEqual)
                : []).map(async (faqItem) => {
                const renderedAnswer = faqItem?.data?.length && faqItem?.data[0]?.answer
                    ? await renderStrapiBlocks({ content: faqItem.data[0].answer as Array<StrapiBlock> })
                    : ''

                return {
                    '@type': 'Question' as const,
                    'name': faqItem?.data?.[0]?.question as string || '',
                    'acceptedAnswer': {
                        '@type': 'Answer' as const,
                        'text': renderedAnswer
                    }
                }
            })
        )
    })
}

if (import.meta.server) {
    await createSeoSchema()
}
</script>

<template>
    <div class="dynamic-content">
        <template v-for="(child, childIndex) in props.content" :key="`${child.__component}${childIndex}`">
            <LazyVisibleNimbusStrapiFullScreenVideo
                v-if="child.__component === 'dynamic.full-screen-video'"
                :video="child"
                hydrate-on-visible
            />

            <LazyVisibleNimbusStrapiCenteredText
                v-else-if="child.__component === 'dynamic.centered-text'"
                :centered-text="child"
                hydrate-on-visible
            />

            <div
                v-else
                :class="{ 'dynamic-content--contained': !!maxWidth }"
            >
                <LazyVisibleNimbusStrapiMediaText
                    v-if="child.__component === 'dynamic.media-text'"
                    :media-text="child"
                    hydrate-on-visible
                />

                <div
                    v-if="child.__component === 'dynamic.rich-text' && child.richtext"
                    class="rich-text"
                    :class="{ 'panel panel__body panel--gray': child.panel }"
                >
                    <LazyNimbusStrapiBlockRenderer :content="child.richtext as Array<StrapiBlock>" hydrate-on-visible />
                </div>

                <LazyVisibleNimbusStrapiGallery
                    v-if="child.__component === 'dynamic.gallery'"
                    :gallery="child"
                    hydrate-on-visible
                />

                <LazyVisibleNimbusStrapiPromotion
                    v-if="child.__component === 'dynamic.promotion'"
                    :promotion="child"
                    hydrate-on-visible
                />

                <LazyNimbusStrapiGameList
                    v-if="child.__component === 'dynamic.game-list'"
                    :game-list-key="(child.gameList as StrapiComponents['schemas']['GameList'])?.key || 'key-not-set'"
                    :game-list="child.gameList as StrapiComponents['schemas']['GameList']"
                />

                <LazyVisibleNimbusStrapiWinnersSidebar
                    v-if="child.__component === 'dynamic.winners-sidebar'"
                    :winners-sidebar-key="(child.winnersSidebar as StrapiComponents['schemas']['WinnersSidebar'])?.key || 'key-not-set'"
                    :winners-sidebar="child.winnersSidebar as StrapiComponents['schemas']['WinnersSidebar']"
                    hydrate-on-visible
                />

                <LazyVisibleNimbusStrapiVendorGames
                    v-if="child.__component === 'dynamic.vendor-list'"
                    :vendor-list="child.vendorList"
                    static
                    hydrate-on-visible
                />

                <LazyVisibleNimbusStrapiUspBar
                    v-if="child.__component === 'shared.header' && child.showStrapiUspBar"
                    :usps="child.usp"
                    hydrate-on-visible
                />

                <LazyVisibleNimbusStrapiTextBlock
                    v-if="child.__component === 'dynamic.text-block'"
                    :text-block="child"
                    hydrate-on-visible
                />

                <LazyVisibleNimbusStrapiBlogPosts
                    v-if="child.__component === 'dynamic.blog-posts'"
                    :blog-posts="child"
                    hydrate-on-visible
                />

                <template v-if="child.__component === 'dynamic.faq'">
                    <LazyVisibleNimbusStrapiFaq :sections="child.faqSections as StrapiComponents['schemas']['Faq'][]" hydrate-on-visible />
                </template>

                <template v-if="child.__component === 'dynamic.content-placeholder' && child.contentSlug">
                    <slot :name="child.contentSlug" />
                </template>
            </div>
        </template>
    </div>
</template>

<style scoped lang="scss">
.dynamic-content {
    color: v-bind(color);

    > div:not(.panel__body) {
        padding-top: 24px;
        padding-bottom: 24px;
    }

    > .faq {
        padding: 4px 0;
    }

    &--contained {
        max-width: v-bind(maxWidth);
        margin: 0 auto;
    }
}
</style>
